import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import {
  type CampaignSource,
  useSelectedCampaignId,
  useCampaignActions,
  useIsHeaderCollapsed,
  useCampaignTypeContext,
} from 'features/campaigns';
import { useAppStore } from 'store/app/appStore';
import {
  stopMomentumScroll,
  disableScrollSnap,
  restoreScrollSnap,
  getCenteredScrollLeft,
} from './carouselScrollHelpers';
import { useOffscreenObserver } from './useOffscreenObserver';
import { useScrollTracking } from './useScrollTracking';

const DESKTOP_BREAKPOINT = 1024;

interface UseCampaignsCarouselProps {
  campaigns: CampaignSource[];
  onCampaignSelect?: (campaign: CampaignSource) => void;
}

export function useCampaignsCarousel({ campaigns, onCampaignSelect }: UseCampaignsCarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);

  // Section type from context (carousel always renders under a section provider);
  // needed for the selection writes below, not just reads.
  const campaignType =  useCampaignTypeContext();

  const selectedCampaignId = useSelectedCampaignId(campaignType);
  const { setSelectedCampaignId } = useCampaignActions();
  const isCollapsed = useIsHeaderCollapsed();
  const isIngameCampaignOnlyOpened = useAppStore((state) => state.isIngameCampaignOnlyOpened);

  const [scrollArrows, setScrollArrows] = useState({
    showLeftArrow: false,
    showRightArrow: false,
    isHovered: false,
  });

  // Visual active state — updates immediately during scroll.
  // The store update is debounced to avoid interrupting iOS scroll compositor.
  const [visualActiveId, setVisualActiveId] = useState<string | null>(null);
  const visualActiveIdRef = useRef<string | null>(null);

  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > DESKTOP_BREAKPOINT);
  const isManualScrollingRef = useRef(false);
  const isProgrammaticScroll = useRef(false);
  const targetCampaignId = useRef<string | null>(null);
  const selectionTimerRef = useRef<NodeJS.Timeout>(undefined);

  const onCampaignSelectRef = useRef(onCampaignSelect);
  onCampaignSelectRef.current = onCampaignSelect;

  // Sync visual state when store changes externally
  useEffect(() => {
    if (selectedCampaignId) {
      setVisualActiveId(selectedCampaignId);
      visualActiveIdRef.current = selectedCampaignId;
    }
  }, [selectedCampaignId]);

  const elementCache = useRef<Map<string, HTMLElement>>(new Map());

  const getCachedElement = useCallback((campaignId: string): HTMLElement | null => {
    if (!carouselRef.current) return null;

    let element = elementCache.current.get(campaignId);
    if (!element || !element.isConnected) {
      element = carouselRef.current.querySelector(`[data-campaign-id="${campaignId}"]`) as HTMLElement;
      if (element) {
        elementCache.current.set(campaignId, element);
      }
    }
    return element || null;
  }, []);

  const scrollToCampaign = useCallback((campaignId: string, smooth = true) => {
    const container = carouselRef.current;
    if (!container) return;

    const campaignElement = getCachedElement(campaignId);
    if (!campaignElement) return;

    isProgrammaticScroll.current = true;
    targetCampaignId.current = campaignId;
    const left = getCenteredScrollLeft(container, campaignElement);

    if (smooth) {
      container.scrollTo({ left, behavior: 'smooth' });
    } else {
      // Direct assignment — always synchronous. scrollTo({ behavior: 'auto' })
      // can be deferred by Safari's compositor (-webkit-overflow-scrolling).
      container.scrollLeft = left;
    }
  }, [getCachedElement]);

  const scrollToCampaignInstant = useCallback((campaignId: string, restoreSnap = true) => {
    const container = carouselRef.current;
    if (!container) return;

    disableScrollSnap(container);
    scrollToCampaign(campaignId, false);

    if (restoreSnap) {
      requestAnimationFrame(() => restoreScrollSnap(container));
    }
  }, [scrollToCampaign]);

  const handleCampaignSelect = useCallback((campaign: CampaignSource) => {
    clearTimeout(selectionTimerRef.current);
    setVisualActiveId(campaign.id);
    visualActiveIdRef.current = campaign.id;
    isManualScrollingRef.current = false;
    isProgrammaticScroll.current = true;
    setSelectedCampaignId(campaign.id, campaignType);
    scrollToCampaign(campaign.id);
    onCampaignSelectRef.current?.(campaign);
  }, [setSelectedCampaignId, scrollToCampaign]);

  // Default selection
  useEffect(() => {
    if (!selectedCampaignId && campaigns.length > 0) {
      setSelectedCampaignId(campaigns[0].id, campaignType);
    }
  }, [selectedCampaignId, campaigns, setSelectedCampaignId, campaignType]);

  // Initial scroll — covers both multi-view entry and in-game campaign-only layout
  const hasMountedRef = useRef(false);
  const prevIsIngameCampaignOnlyOpenedRef = useRef(false);

  useLayoutEffect(() => {
    if (!selectedCampaignId) return;

    const isMultiViewEntry = !hasMountedRef.current && !isIngameCampaignOnlyOpened;
    const isIngameEntry = !prevIsIngameCampaignOnlyOpenedRef.current && isIngameCampaignOnlyOpened;

    if (isMultiViewEntry || isIngameEntry) {
      hasMountedRef.current = true;
      isProgrammaticScroll.current = true;
      scrollToCampaignInstant(selectedCampaignId);
    } 
    prevIsIngameCampaignOnlyOpenedRef.current = isIngameCampaignOnlyOpened;
  }, [selectedCampaignId, scrollToCampaign, isIngameCampaignOnlyOpened]);

  // Collapse / expand — re-center with Safari momentum + snap workarounds
  const prevCollapsedRef = useRef(isCollapsed);
  const collapseCleanupRef = useRef<NodeJS.Timeout>(undefined);

  useLayoutEffect(() => {
    if (prevCollapsedRef.current === isCollapsed || !carouselRef.current) {
      prevCollapsedRef.current = isCollapsed;
      return;
    }
    prevCollapsedRef.current = isCollapsed;

    const container = carouselRef.current;
    container.setAttribute('data-animating', 'true');

    stopMomentumScroll(container);
    isManualScrollingRef.current = false;
    clearTimeout(selectionTimerRef.current);

    // Use visual ID (always current) over store ID (debounced)
    const activeId = visualActiveIdRef.current || selectedCampaignId;

    if (activeId) {
      isProgrammaticScroll.current = true;
      targetCampaignId.current = activeId;
      scrollToCampaignInstant(activeId, false);

      if (activeId !== selectedCampaignId) {
        setSelectedCampaignId(activeId);
      }
    }

    clearTimeout(collapseCleanupRef.current);
    collapseCleanupRef.current = setTimeout(() => {
      container.removeAttribute('data-animating');
      if (activeId) scrollToCampaign(activeId, false);
      restoreScrollSnap(container);
      isProgrammaticScroll.current = false;
      targetCampaignId.current = null;
    }, 300);
  }, [isCollapsed, selectedCampaignId, scrollToCampaignInstant, scrollToCampaign, setSelectedCampaignId]);

  // Resize — re-center active card
  useEffect(() => {
    let resizeRaf = 0;
    const handleResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        setIsDesktop(window.innerWidth > DESKTOP_BREAKPOINT);
        if (selectedCampaignId) scrollToCampaignInstant(selectedCampaignId);
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(resizeRaf);
    };
  }, [selectedCampaignId, scrollToCampaignInstant]);

  // Desktop hover arrows
  const updateArrowVisibility = useCallback(() => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.offsetWidth;
    const threshold = 10;
    setScrollArrows(prev => ({
      ...prev,
      showLeftArrow: scrollLeft > threshold,
      showRightArrow: scrollLeft < maxScrollLeft - threshold,
    }));
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isDesktop && campaigns.length > 3) {
      setScrollArrows(prev => ({ ...prev, isHovered: true }));
      updateArrowVisibility();
    }
  }, [isDesktop, campaigns.length, updateArrowVisibility]);

  const handleMouseLeave = useCallback(() => {
    setScrollArrows(prev => ({
      ...prev,
      isHovered: false,
      showLeftArrow: false,
      showRightArrow: false,
    }));
  }, []);

  const registerCarouselItem = useOffscreenObserver(carouselRef);

  useScrollTracking({
    carouselRef,
    campaigns,
    isArrowsHovered: scrollArrows.isHovered,
    refs: { isProgrammaticScroll, targetCampaignId, isManualScrollingRef, visualActiveIdRef, selectionTimerRef },
    getCachedElement,
    setSelectedCampaignId,
    setVisualActiveId,
    setScrollArrows,
    onCampaignSelectRef,
  });

  return {
    carouselRef,
    selectedCampaignId: visualActiveId ?? selectedCampaignId,
    isDesktop,
    handleMouseEnter,
    handleMouseLeave,
    handleCampaignSelect,
    registerCarouselItem,
  };
}
